/**
 * Antimeridian-safe line geometry — keep a polyline continuous when a
 * Producer's track crosses the ±180° date line.
 *
 * # The bug this fixes
 *
 * MapLibre draws a ``LineString`` by connecting consecutive vertices in
 * projected space. WGS84 longitudes are normalized to ``[-180, 180]``, so a
 * Producer that moves east across the date line reports e.g. ``179.8`` then
 * ``-179.8`` — a raw delta of ``-359.6°``. Connecting those two vertices
 * directly paints a near-full-width horizontal line straight across the map
 * instead of the ~0.4° hop across the seam (the reported track-tail bug).
 *
 * # The fix: unwrap, don't split
 *
 * Rather than splitting the line at the seam (which needs interpolated
 * vertices exactly on ±180° and produces multiple features), we **unwrap**
 * the longitude sequence so consecutive points never jump more than 180°.
 * The ``179.8 → -179.8`` step becomes ``179.8 → 180.2``. MapLibre renders
 * vertices outside ``[-180, 180]`` at their literal projected position, and
 * with ``renderWorldCopies`` (on by default) the segment shows on the
 * opposite edge too — so the operator sees the tail cross the date line the
 * short way, on both sides of the seam. This is the standard MapLibre/Mapbox
 * antimeridian treatment.
 *
 * The unwrapped longitude can grow past ±180° by 360° per crossing, which is
 * exactly what MapLibre wants; callers trim tracks to a small km budget
 * upstream, so at most one or two crossings accumulate.
 *
 * Cross-references:
 *   - REQ-UI-012 (trail rendering) / REQ-UI-002 (MapLibre map).
 *   - ADR-016 §5 (trail/track-tail scope).
 *   - map/track-tail-layer-impl.ts, map/trail-styling.ts (the two callers).
 */

/** Minimal positional shape both ``TrackPoint`` and ``TrailItem`` satisfy. */
export interface LonLatPoint {
  /** WGS84 longitude in decimal degrees, normalized to [-180, 180]. */
  longitude_deg: number;
  /** WGS84 latitude in decimal degrees. */
  latitude_deg: number;
}

/**
 * Unwrap a sequence of normalized longitudes so the step between consecutive
 * values is always the shortest one (in ``(-180, 180]``), accumulated onto a
 * continuous running value. The first element is returned unchanged; each
 * subsequent element is the previous unwrapped value plus the shortest-path
 * delta to the next raw longitude.
 *
 * Empty / single-element inputs are returned as-is (no step to unwrap).
 *
 * @example
 *   unwrapLongitudes([179.8, -179.8]) // → [179.8, 180.2]
 *   unwrapLongitudes([-179, 179])     // → [-179, -181]
 *
 * Cross-references:
 *   - REQ-UI-012.
 */
export function unwrapLongitudes(longitudes: readonly number[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < longitudes.length; i += 1) {
    const lon = longitudes[i]!;
    if (i === 0) {
      out.push(lon);
      continue;
    }
    const rawDelta = lon - longitudes[i - 1]!;
    // Normalize the step to (-180, 180] — the shortest way around the globe.
    const delta = rawDelta - Math.round(rawDelta / 360) * 360;
    out.push(out[i - 1]! + delta);
  }
  return out;
}

/**
 * Project a time-ordered point sequence to MapLibre ``[lon, lat]`` pairs with
 * antimeridian-unwrapped longitudes, so the resulting ``LineString`` stays
 * continuous across the ±180° seam.
 *
 * This is the single shared seam-safe coordinate builder; both the dynamic
 * track-tail layer and the selected-Producer trail layer route their
 * ``LineString`` coordinates through it (no duplicated unwrap logic per
 * CLAUDE.md §4.4).
 *
 * Cross-references:
 *   - REQ-UI-012.
 */
export function toUnwrappedCoordinates(points: readonly LonLatPoint[]): [number, number][] {
  const lons = unwrapLongitudes(points.map((p) => p.longitude_deg));
  return points.map((p, i) => [lons[i]!, p.latitude_deg]);
}
